<script>
export default {
    name: "LogModal",
    props: {
        modalTitle: {
            type: String,
            default: "Activity Logs",
        },
        modalId: {
            type: String,
            default: "log-modal",
        },
        size: {
            type: String,
            default: "lg",
        },
    },
    data() {
        return {
            tableData: [],
            tableBusy: false,
            customTitle: "",
            search: "",
        };
    },
    computed: {
        displayTitle() {
            return this.customTitle || this.modalTitle || "Activity Logs";
        },
        rows() {
            return this.tableData.length;
        },
        filteredLogs() {
            if (!this.search || !this.search.trim()) {
                return this.tableData;
            }
            const q = this.search.toLowerCase().trim();
            return this.tableData.filter((x) => {
                const actionMatch = x.action && x.action.toLowerCase().includes(q);
                const userMatch = x.loggedByUser && x.loggedByUser.toLowerCase().includes(q);
                const dateMatch = x.dateWTime && x.dateWTime.toLowerCase().includes(q);
                const typeMatch = x.logType && x.logType.toLowerCase().includes(q);
                const changeMatch =
                    x.changes &&
                    x.changes.some(
                        (c) =>
                            c.field.toLowerCase().includes(q) ||
                            c.from.toLowerCase().includes(q) ||
                            c.to.toLowerCase().includes(q)
                    );
                return actionMatch || userMatch || dateMatch || typeMatch || changeMatch;
            });
        },
    },
    methods: {
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        reset() {
            this.search = "";
            this.customTitle = "";
        },
        /**
         * Standard method to receive logs from any component/page
         * Can receive an Array, an Object with logs property, or a JSON string.
         */
        setLogs(logsData, title = "") {
            if (title) {
                this.customTitle = title;
            }
            this.tableBusy = true;
            try {
                const rawList = this.extractRawLogs(logsData);
                const normalized = rawList.map((item, index) => this.normalizeLog(item, index));
                // Sort descending by date (newest first)
                this.tableData = normalized.sort((a, b) => {
                    const timeA = a.dateTime ? new Date(a.dateTime).getTime() : 0;
                    const timeB = b.dateTime ? new Date(b.dateTime).getTime() : 0;
                    return timeB - timeA;
                });
            } catch (err) {
                console.error("Error setting logs in LogModal:", err);
                this.tableData = [];
            } finally {
                this.tableBusy = false;
            }
        },
        updateData(data, title = "") {
            this.setLogs(data, title);
        },
        /**
         * Programmatically opens the modal and sets log data
         */
        open(options = {}) {
            if (Array.isArray(options)) {
                this.setLogs(options);
            } else if (typeof options === "object" && options !== null) {
                const title = options.title || options.modalTitle || "";
                const data = options.logs || options.data || options;
                this.setLogs(data, title);
            }
            this.$bvModal.show(this.modalId);
        },
        /**
         * Fetch or dispatch data if a store action or ID is passed
         */
        async getData(actionOrId, options = {}) {
            this.tableBusy = true;
            this.$bvModal.show(this.modalId);
            if (options.title) {
                this.customTitle = options.title;
            }
            try {
                if (typeof actionOrId === "string" && actionOrId.includes("/")) {
                    const { data: res } = await this.$store.dispatch(actionOrId, options.payload || options.id);
                    this.setLogs(res);
                } else if (Array.isArray(actionOrId) || (typeof actionOrId === "object" && actionOrId !== null)) {
                    this.setLogs(actionOrId);
                }
            } catch (e) {
                console.error("Error in LogModal getData:", e);
                this.tableData = [];
            } finally {
                this.tableBusy = false;
            }
        },
        /**
         * Extract raw array from multiple potential structures
         */
        extractRawLogs(input) {
            if (!input) return [];

            if (typeof input === "string") {
                try {
                    input = JSON.parse(input);
                } catch (e) {
                    return [];
                }
            }

            if (Array.isArray(input)) {
                return input;
            }

            if (input && typeof input === "object") {
                if (input.targetLogs) {
                    return this.extractRawLogs(input.targetLogs);
                }
                if (input.indicatorItemLogs) {
                    return this.extractRawLogs(input.indicatorItemLogs);
                }
                if (input.logs) {
                    return this.extractRawLogs(input.logs);
                }
                if (input.data) {
                    return this.extractRawLogs(input.data);
                }
            }

            return [];
        },
        /**
         * Uniform log data representation across the application
         */
        normalizeLog(item, index) {
            if (!item || typeof item !== "object") {
                return {
                    id: `log-${index}`,
                    rawDate: null,
                    dateTime: null,
                    logDate: null,
                    dateWTime: "",
                    splitted: ["", ""],
                    loggedByUser: "Unknown User",
                    loggedBy: "Unknown User",
                    action: String(item),
                    remarks: String(item),
                    logType: "",
                    statusBadge: null,
                    title: String(item),
                    changes: [],
                    raw: item,
                };
            }

            const rawDate = item.dateTime || item.logDate || item.date || item.createdAt || item.timestamp || null;
            const user =
                item.loggedByUser ||
                item.loggedBy ||
                item.applicationUser?.completeName ||
                item.user ||
                item.userName ||
                "";
            const actionText = item.action || item.remarks || item.description || item.message || item.logType || "";
            const formattedAction = this.formatActionText(actionText);
            const logType = item.logType || item.type || "";

            const { dateWTime, splitted } = this.formatLogDate(rawDate);
            const changes = this.parseChanges(actionText);
            const title = this.cleanActionTitle(actionText);
            const statusBadge = this.resolveStatusBadge(logType, actionText);

            return {
                id: item.id || `log-${index}-${Date.now()}`,
                rawDate: rawDate,
                dateTime: rawDate,
                logDate: rawDate,
                dateWTime: dateWTime,
                splitted: splitted,
                loggedByUser: user ? user.trim() : "",
                loggedBy: user ? user.trim() : "",
                action: formattedAction,
                rawAction: actionText,
                remarks: formattedAction,
                logType: logType,
                statusBadge: statusBadge,
                title: title,
                changes: changes,
                raw: item,
            };
        },
        formatLogDate(dateVal) {
            if (!dateVal) return { dateWTime: "", splitted: ["", ""] };
            const dt = new Date(dateVal);
            if (isNaN(dt.getTime())) {
                return { dateWTime: String(dateVal), splitted: [String(dateVal), ""] };
            }
            let dateWTime = "";
            if (typeof this.formatDateWithTime === "function") {
                dateWTime = this.formatDateWithTime(dt);
            } else {
                dateWTime = dt.toLocaleString();
            }

            let splitted = ["", ""];
            if (dateWTime.includes(" at ")) {
                splitted = dateWTime.split(" at ");
            } else if (dateWTime.includes(", ")) {
                const parts = dateWTime.split(", ");
                splitted = [parts.slice(0, -1).join(", "), parts[parts.length - 1]];
            } else {
                splitted = [dateWTime, ""];
            }
            return { dateWTime, splitted };
        },
        formatActionText(text) {
            if (!text || typeof text !== "string") return "";
            return text.replace(/(?:([a-zA-Z0-9_\s]+):\s*)?['"]?([^'"\n\r]+?)['"]?\s*->\s*['"]?([^'"\n\r,]+)['"]?/g, (match, field, from, to) => {
                const f = field ? `${field.trim()}: ` : "";
                return `${f}Change from ${from.trim()} to ${to.trim()}`;
            });
        },
        parseChanges(text) {
            if (!text || typeof text !== "string") return [];
            const regex = /(?:([a-zA-Z0-9_\s]+):\s*)?['"]?([^'"\n\r]+?)['"]?\s*->\s*['"]?([^'"\n\r,]+)['"]?/g;
            const changes = [];
            let match;
            while ((match = regex.exec(text)) !== null) {
                const field = match[1] ? match[1].trim() : "";
                const from = match[2].trim();
                const to = match[3].trim();
                changes.push({
                    field: field,
                    from: from,
                    to: to,
                    displayText: `Change from ${from} to ${to}`,
                });
            }
            return changes;
        },
        cleanActionTitle(text) {
            if (!text || typeof text !== "string") return "";
            let prefix = text;
            const match = text.search(/[a-zA-Z0-9_]+:\s*['"]?[^'"\n\r]+['"]?\s*->/);
            if (match !== -1) {
                prefix = text.substring(0, match).trim();
            }
            prefix = prefix.replace(/[-:, \t]+$/, "").trim();

            const parts = prefix.split(/\s*-\s*/).filter(Boolean);
            const uniqueParts = [];
            for (const p of parts) {
                if (!uniqueParts.includes(p)) {
                    uniqueParts.push(p);
                }
            }
            return uniqueParts.join(" - ") || prefix || text;
        },
        resolveStatusBadge(logType, action) {
            const combined = `${logType || ""} ${action || ""}`.toLowerCase();
            if (combined.includes("return")) {
                return { label: "Returned", class: "badge bg-warning bg-soft text-warning" };
            }
            if (combined.includes("approve")) {
                return { label: "Approved", class: "badge bg-success bg-soft text-success" };
            }
            if (combined.includes("delete") || combined.includes("remove")) {
                return { label: "Deleted", class: "badge bg-danger bg-soft text-danger" };
            }
            if (combined.includes("create") || combined.includes("add")) {
                return { label: "Created", class: "badge bg-info bg-soft text-info" };
            }
            if (combined.includes("update") || combined.includes("edit") || combined.includes("change")) {
                return { label: "Updated", class: "badge bg-primary bg-soft text-primary py-1" };
            }
            if (logType) {
                return { label: logType, class: "badge bg-secondary bg-soft text-secondary" };
            }
            return null;
        },
        hasStatus(item, status) {
            const text = `${item.logType || ""} ${item.action || ""}`.toLowerCase();
            return text.includes(status.toLowerCase());
        },
    },
};
</script>

<template>
    <b-modal :id="modalId" :title="displayTitle" :size="size" title-class="font-18" hide-footer no-close-on-backdrop
        no-close-on-esc>
        <!-- Search bar (if multiple logs exist) -->
        <div class="d-flex justify-content-between align-items-center mb-3 px-2" v-if="tableData.length > 2">
            <span class="text-muted font-size-12">
                Showing {{ filteredLogs.length }} of {{ tableData.length }}
                {{ tableData.length === 1 ? "event" : "events" }}
            </span>
            <div style="max-width: 260px;" class="ms-auto">
                <b-form-input v-model="search" type="search" placeholder="Search logs..." size="sm"
                    class="form-control-sm"></b-form-input>
            </div>
        </div>

        <!-- Loading State -->
        <div class="text-center py-5" v-if="tableBusy">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <div class="mt-2 text-muted font-size-13">Loading logs...</div>
        </div>

        <!-- Empty State -->
        <div class="text-center py-5" v-else-if="tableData.length === 0">
            <i class="bx bx-receipt font-size-24 text-muted d-block mb-2"></i>
            <h5 class="text-muted mb-1">No logs available</h5>
            <p class="text-muted font-size-12 mb-0">There are no activity logs recorded yet.</p>
        </div>

        <!-- Search No Results State -->
        <div class="text-center py-4" v-else-if="filteredLogs.length === 0">
            <i class="bx bx-search font-size-24 text-muted d-block mb-2"></i>
            <p class="text-muted mb-0 font-size-13">
                No logs matching "<strong>{{ search }}</strong>"
            </p>
        </div>

        <!-- Timeline Body -->
        <div class="log-timeline-container px-2" style="max-height: 65vh; overflow-y: auto;" v-else>
            <div class="d-flex justify-content-center">
                <ul class="verti-timeline list-unstyled w-100 mb-0">
                    <li class="event-list" v-for="(x, xIndex) in filteredLogs" :key="x.id"
                        :class="{ active: xIndex === 0 }">
                        <div class="event-timeline-dot">
                            <i class="bx bx-right-arrow-circle font-size-18"
                                :class="{ 'bx-fade-right': xIndex === 0 }"></i>
                        </div>
                        <div class="media">
                            <!-- Date & Time Column -->
                            <div class="me-3" style="min-width: 140px; max-width: 170px;">
                                <h5 class="font-size-13 mb-0 text-dark">
                                    {{ x.splitted[0] }}
                                    <i class="bx bx-right-arrow-alt font-size-15 text-primary align-middle ms-1"></i>
                                </h5>
                                <small class="text-muted font-size-12">{{ x.splitted[1] }}</small>
                            </div>

                            <!-- Content Column -->
                            <div class="media-body">
                                <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span v-if="x.statusBadge" :class="x.statusBadge.class"
                                        class="px-2 py-0 rounded font-weight-bold font-size-11">
                                        {{ x.statusBadge.label }}
                                    </span>
                                    <h6 class="font-size-13 font-weight-bold mb-0 text-body">
                                        {{ x.title || x.action }}
                                    </h6>
                                </div>

                                <!-- Parsed Structured Changes -->
                                <div v-if="x.changes && x.changes.length > 0"
                                    class="mt-2 p-2 rounded change-box font-size-12">
                                    <div v-for="(c, cIndex) in x.changes" :key="cIndex" class="py-1"
                                        :class="{ 'border-bottom': cIndex < x.changes.length - 1 }">
                                        <span class="text-secondary font-weight-semibold" v-if="c.field">{{ c.field }}:
                                        </span>
                                        <span>Change from <span class="font-weight-semibold">{{ c.from }}</span> to
                                            <span class="font-weight-semibold">{{ c.to }}</span></span>
                                    </div>
                                </div>

                                <!-- Fallback Action Description -->
                                <div v-else-if="x.action && x.action !== x.title"
                                    class="text-muted font-size-12 mt-1 mb-0" style="line-height: 1.4;">
                                    {{ x.action }}
                                </div>

                                <!-- Logged By User -->
                                <div class="mt-1" v-if="x.loggedByUser">
                                    <small class="text-muted d-inline-flex align-items-center">
                                        <i class="bx bx-user me-1 text-primary"></i>
                                        <span>{{ x.loggedByUser }}</span>
                                    </small>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Modal Footer -->
        <div class="d-flex justify-content-end mt-3 pt-2 border-top">
            <b-button variant="light" size="sm" @click="hideModal">Close</b-button>
        </div>
    </b-modal>
</template>

<style scoped>
.log-timeline-container {
    scrollbar-width: thin;
}

.change-box {
    background-color: var(--bs-card-bg, rgba(130, 130, 130, 0.08));
    border: 1px solid rgba(130, 130, 130, 0.18);
}
</style>
